'use client';
import { useEffect, useRef } from 'react';
import { useCart } from '@/contexts/CartContext';
import elevarItemView from '@/lib/elevar/item-view';
import elevarPageType from '@/lib/elevar/page-type';
import { elevarBaseData } from '@/lib/elevar/cart-events';
import { elevarEnsureContext } from '@/lib/elevar/ensure-context';
import { useProduct } from '@/contexts/ProductContext';
import { usePricing } from '@/contexts/PricingContext';

/**
 * Fires `dl_user_data` once per mount, then `dl_view_item`/legacy `pageType`
 * once per distinct `selectedVariant` (including variant changes). Mount
 * with `key={slug}` to reset both refs on nav. Waits on
 * `hasResolvedPersistedOptions` so the first fire uses the real variant.
 * A ref is marked before its push fires, so a rerun mid-flight won't
 * duplicate it.
 */
export default function ElevarProduct() {
  const { cart } = useCart();
  const pricing = usePricing();
  const { productData, selectedVariant, hasResolvedPersistedOptions } = useProduct();
  const hasFiredBaseDataRef = useRef(false);
  const firedVariantIdRef = useRef(null);

  useEffect(() => {
    if (cart === null || !hasResolvedPersistedOptions) return;
    if (firedVariantIdRef.current === selectedVariant?.id) return;

    let cancelled = false;

    (async () => {
      await elevarEnsureContext();
      if (cancelled) return;

      if (!hasFiredBaseDataRef.current) {
        hasFiredBaseDataRef.current = true;
        await elevarBaseData(cart, pricing);
        if (cancelled) return;
      }

      firedVariantIdRef.current = selectedVariant?.id;
      elevarItemView(productData, selectedVariant, pricing);
      elevarPageType(productData, selectedVariant, pricing);
    })();

    return () => { cancelled = true; };
  }, [cart, productData, selectedVariant, hasResolvedPersistedOptions]);

  return null;
}
